/**
 * Tourist spot service — API-first.
 *
 * When VITE_PUBLIC_API_URL is set, every call hits the Node + Express + MariaDB
 * backend; the spots now live in the `tourist_spots` table instead of the mock
 * bundle. Backend URL map:
 *   GET  /spots                     list (district, category, featured, limit)
 *   GET  /spots/featured?limit=8    featured strip
 *   GET  /spots/districts           districts + counts
 *   GET  /spots/:slug               single spot
 *   POST /spots/:id/view            record a view
 *
 * When VITE_PUBLIC_API_URL is empty → the bundled mock data is used so the UI
 * always renders in preview/demo mode. When an API is configured, failures
 * PROPAGATE so the UI shows a real error + retry state.
 */

import { touristSpots } from "@/mocks/touristSpots";
import type { TouristSpot } from "@/mocks/touristSpots";
import { apiGet, apiPost, isApiConfigured, ApiError } from "./http";

export type { TouristSpot };

const DEMO_DELAY = 100;

function delay(ms = DEMO_DELAY): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

// ── Backend adapter ───────────────────────────────────────────────────────────

interface BackendSpot {
  id:            number;
  slug:          string;
  name:          string;
  districtSlug:  string;
  districtName:  string;
  category:      string;
  blurb:         string;
  description:   string;
  image:         string;
  timings:       string;
  entryFee:      string;
  bestTime:      string;
  idealDuration: string;
  address:       string;
  highlights:    string[];
  featured:      boolean;
}

function adaptSpot(s: BackendSpot): TouristSpot {
  return {
    id:            String(s.id),
    slug:          s.slug,
    name:          s.name,
    districtSlug:  s.districtSlug,
    districtName:  s.districtName,
    category:      s.category ?? "",
    blurb:         s.blurb ?? "",
    description:   s.description ?? "",
    image:         s.image ?? "",
    timings:       s.timings ?? "",
    entryFee:      s.entryFee ?? "",
    bestTime:      s.bestTime ?? "",
    idealDuration: s.idealDuration ?? "",
    address:       s.address ?? "",
    highlights:    Array.isArray(s.highlights) ? s.highlights : [],
    featured:      Boolean(s.featured),
  };
}

/** Shared demo adapter — only used when no API URL is configured. */
async function demoOnly<T>(live: () => Promise<T>, demo: () => T): Promise<T> {
  if (isApiConfigured()) return live();
  await delay();
  return demo();
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Every published tourist spot in the directory. */
export function getTouristSpots(): Promise<TouristSpot[]> {
  return demoOnly(
    async () => (await apiGet<BackendSpot[]>("/spots")).map(adaptSpot),
    () => [...touristSpots],
  );
}

/** Spots that belong to a given district. */
export function getSpotsByDistrict(districtSlug: string, limit?: number): Promise<TouristSpot[]> {
  return demoOnly(
    async () => {
      const list = await apiGet<BackendSpot[]>(`/spots?district=${encodeURIComponent(districtSlug)}`);
      const mapped = list.map(adaptSpot);
      return typeof limit === "number" ? mapped.slice(0, limit) : mapped;
    },
    () => {
      const list = touristSpots.filter((spot) => spot.districtSlug === districtSlug);
      return typeof limit === "number" ? list.slice(0, limit) : list;
    },
  );
}

/** A single spot by its URL slug. */
export async function getSpotBySlug(slug: string): Promise<TouristSpot | null> {
  if (!isApiConfigured()) {
    await delay();
    return touristSpots.find((spot) => spot.slug === slug) ?? null;
  }
  try {
    const spot = await apiGet<BackendSpot>(`/spots/${encodeURIComponent(slug)}`);
    return adaptSpot(spot);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** Editorially featured spots for the home page strip. */
export function getFeaturedSpots(limit = 8): Promise<TouristSpot[]> {
  return demoOnly(
    async () => {
      const list = await apiGet<BackendSpot[]>(`/spots/featured?limit=${limit}`);
      if (list.length) return list.map(adaptSpot);
      // Empty featured set → fall back to the full published list (first N).
      const all = await apiGet<BackendSpot[]>("/spots");
      return all.map(adaptSpot).slice(0, limit);
    },
    () => {
      const featured = touristSpots.filter((spot) => spot.featured);
      return (featured.length ? featured : touristSpots).slice(0, limit);
    },
  );
}

/** Other spots in the same district, excluding the current one. */
export async function getNearbySpots(
  districtSlug: string,
  excludeSlug: string,
  limit = 4,
): Promise<TouristSpot[]> {
  const inDistrict = await getSpotsByDistrict(districtSlug);
  return inDistrict.filter((spot) => spot.slug !== excludeSlug).slice(0, limit);
}

/** All districts that currently have spots, for browse links. */
export function getSpotDistricts(): Promise<{ slug: string; name: string; count: number }[]> {
  return demoOnly(
    () => apiGet<{ slug: string; name: string; count: number }[]>("/spots/districts"),
    () => {
      const map = new Map<string, { slug: string; name: string; count: number }>();
      touristSpots.forEach((spot) => {
        const existing = map.get(spot.districtSlug);
        if (existing) {
          existing.count += 1;
        } else {
          map.set(spot.districtSlug, {
            slug: spot.districtSlug,
            name: spot.districtName,
            count: 1,
          });
        }
      });
      return Array.from(map.values());
    },
  );
}

/** Record a public view for a spot (best-effort, never throws). */
export async function recordSpotView(spotId: string | number): Promise<void> {
  if (!isApiConfigured()) return;
  try {
    await apiPost(`/spots/${spotId}/view`, {});
  } catch {
    /* analytics ping — safe to ignore */
  }
}